import {
  Activity,
  Briefcase,
  Building2,
  Copy,
  Fingerprint,
  Globe,
  Mail,
  Pencil,
  Phone,
  Plus,
  Save,
  ShieldAlert,
  ShieldX,
  Tag,
  Target,
  X,
  Zap,
  LineChart,
  BarChart3,
  TrendingUp,
  DollarSign,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import apiService from "../../shared/apiService";
import { cn } from "../../utils/cn";
import { getPlatformName } from "../../utils/platformIcons";
import { Badge, Button, Modal } from "../index";
import { CARD_ACTION, CARD_ACTION_DANGER } from "../organization/actionStyles";

interface CampaignLead {
  id: number;
  campaign_id?: number;
  platform: string;
  campaign?: {
    id: number;
    name: string;
    platform: string | null;
    status: string | null;
    source_post_id?: number | null;
  } | null;
  name?: string;
  email: string;
  phone?: string;
  company?: string;
  job_title?: string;
  notes?: string;
  interest?: string;
  origin_platform?: string;
  origin_description?: string;
  status: string;
  qualification_status?: string;
  lead_score: number;
  created_at: string;
  updated_at?: string;
  is_duplicate?: boolean;
  is_cancelled?: boolean;
  is_fraud?: boolean;
  manual_creation?: boolean;
  tags?: number[];
  custom_fields?: Record<string, unknown>;
  lead_source_system?: string;
  external_lead_id?: string;
  metadata?: Record<string, unknown>;
  leadflow_lead_id?: string | null;
  leadflow_sync_status?: string | null;
}

// E-mails que o SMM inventa quando não tem o da pessoa (igual a real_email no backend).
const PLACEHOLDER_EMAIL =
  /@([a-z0-9-]+\.social|revenue\.tracked|sem-email\.invalid)$/i;

const hasRealContact = (lead: CampaignLead) =>
  !!(lead.phone || "").trim() ||
  (!!(lead.email || "").trim() && !PLACEHOLDER_EMAIL.test(lead.email.trim()));

interface LeadflowAuditEntry {
  event?: string;
  status?: string;
  description?: string;
  timestamp?: string;
  [key: string]: unknown;
}

interface KanbanStage {
  key: string;
  label: string;
  color: string;
}
interface LeadTag {
  id: number;
  name: string;
  color: string;
}

interface EditData {
  name: string;
  email: string;
  phone: string;
  additional_emails: string[];
  additional_phones: string[];
  company: string;
  job_title: string;
  notes: string;
  /** Origem do lead (chave da lista); corrigida aqui, vai ao Leadflow com o histórico. */
  offline_source: string;
}

interface CampaignLeadDetailsModalProps {
  lead: CampaignLead | null;
  onClose: () => void;
  onSyncCapi?: (id: number) => void;
  onSyncCRM?: (id: number) => void;
  onQualificationChange?: (id: number, status: string) => void;
  onStageChange?: (id: number, stage: string) => void;
  onLeadUpdate?: (id: number, data: Partial<EditData>) => Promise<void>;
  onTagsChange?: (id: number, tagIds: number[]) => Promise<void>;
  kanbanStages?: KanbanStage[];
  availableTags?: LeadTag[];
  /** Lista "Como o lead chegou" (para enviar ao LeadFlow um lead sem origem). */
  offlineSources?: { value: string; label: string; group?: string }[];
  /** Envia ao LeadFlow um lead que ainda não está lá. */
  onSendToLeadflow?: (id: number, offlineSource?: string) => Promise<void>;
}

const CampaignLeadDetailsModal: React.FC<CampaignLeadDetailsModalProps> = ({
  lead,
  onClose,
  onSyncCapi,
  onSyncCRM,
  onStageChange,
  onLeadUpdate,
  onTagsChange,
  kanbanStages = [],
  availableTags = [],
  offlineSources = [],
  onSendToLeadflow,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<EditData>({
    name: "",
    email: "",
    phone: "",
    additional_emails: [],
    additional_phones: [],
    company: "",
    job_title: "",
    notes: "",
    offline_source: "",
  });
  const [saving, setSaving] = useState(false);
  const [leadflowSource, setLeadflowSource] = useState("");
  const [sendingToLeadflow, setSendingToLeadflow] = useState(false);
  useEffect(() => setLeadflowSource(""), [lead?.id]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const tagDropRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<"geral" | "revenue">("geral");
  const [revenueData, setRevenueData] = useState<any>(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [leadflowAudit, setLeadflowAudit] = useState<
    LeadflowAuditEntry[] | null
  >(null);
  const [loadingLeadflowAudit, setLoadingLeadflowAudit] = useState(false);
  const [leadflowAuditError, setLeadflowAuditError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (activeTab === "revenue" && lead?.id) {
      const fetchRevenue = async () => {
        setLoadingRevenue(true);
        try {
          const response = await fetch(
            `http://localhost:8002/api/attribution/lead/${lead.id}`,
          );
          if (response.ok) {
            const data = await response.json();
            setRevenueData(data);
          }
        } catch (err) {
          console.error("Failed to load revenue intel", err);
        } finally {
          setLoadingRevenue(false);
        }
      };
      fetchRevenue();
    }
  }, [activeTab, lead?.id]);

  // Auditoria do Leadflow: só existe pra leads capturados via /webhook/leadflow.
  useEffect(() => {
    if (
      activeTab === "revenue" &&
      lead?.id &&
      lead.lead_source_system === "leadflow"
    ) {
      setLeadflowAudit(null);
      setLeadflowAuditError(null);
      setLoadingLeadflowAudit(true);
      apiService
        .get<any>(`/ads/leads/${lead.id}/leadflow-audit`)
        .then((res) => {
          if (res?.success) {
            setLeadflowAudit(res.audit_trail || []);
          } else {
            setLeadflowAuditError(
              res?.error || t("Erro ao buscar auditoria do Leadflow"),
            );
          }
        })
        .catch((err: any) => {
          setLeadflowAuditError(
            err?.message || t("Erro ao buscar auditoria do Leadflow"),
          );
        })
        .finally(() => setLoadingLeadflowAudit(false));
    }
  }, [activeTab, lead?.id, lead?.lead_source_system, t]);

  useEffect(() => {
    if (lead) {
      setEditing(false);
      const cf = lead.custom_fields || {};
      setEditData({
        name: lead.name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        additional_emails: (cf.additional_emails as string[]) || [],
        additional_phones: (cf.additional_phones as string[]) || [],
        company: lead.company || "",
        job_title: lead.job_title || "",
        notes: lead.notes || "",
        offline_source: (lead.metadata?.offline_source as string) || "",
      });
    }
  }, [lead?.id]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (tagDropRef.current && !tagDropRef.current.contains(e.target as Node))
        setTagDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!lead) return null;

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getScoreColor = (score: number) =>
    score >= 80
      ? "text-green-600"
      : score >= 50
        ? "text-blue-600"
        : "text-amber-600";

  const originKey = lead.metadata?.offline_source as string | undefined;
  const originLabel =
    offlineSources.find((o) => o.value === originKey)?.label || originKey;
  // Agrupadas por seção (Indicação, Anúncios, Digital orgânico...).
  const sourceOptions = Object.entries(
    offlineSources.reduce<Record<string, { value: string; label: string }[]>>(
      (acc, o) => {
        (acc[o.group || ""] ||= []).push(o);
        return acc;
      },
      {},
    ),
  ).map(([group, options]) =>
    group ? (
      <optgroup key={group} label={group}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </optgroup>
    ) : (
      options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))
    ),
  );

  const handleSave = async () => {
    if (!onLeadUpdate) return;
    setSaving(true);
    try {
      await onLeadUpdate(lead.id, editData);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const currentTagIds = lead.tags || [];
  const assignedTags = availableTags.filter((t) =>
    currentTagIds.includes(t.id),
  );
  const unassignedTags = availableTags.filter(
    (t) => !currentTagIds.includes(t.id),
  );

  const toggleTag = async (tagId: number, assigned: boolean) => {
    if (!onTagsChange) return;
    const next = assigned
      ? currentTagIds.filter((id) => id !== tagId)
      : [...currentTagIds, tagId];
    await onTagsChange(lead.id, next);
  };

  const inputCls =
    "w-full h-9 px-3 text-sm rounded-lg border border-gray-200 bg-white focus:outline-hidden focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-colors placeholder:text-gray-400";
  const labelCls = "text-xs font-medium text-gray-500 block mb-1";
  const cardCls = "bg-white border border-purple-200 rounded-2xl p-5";
  const cardTitleCls =
    "flex items-center gap-2 text-sm font-semibold text-gray-900 mb-4";

  const additionalEmails = (
    (lead.custom_fields?.additional_emails ?? []) as string[]
  ).filter(Boolean);
  const additionalPhones = (
    (lead.custom_fields?.additional_phones ?? []) as string[]
  ).filter(Boolean);

  const canSendToLeadflow =
    !!onSendToLeadflow &&
    !lead.leadflow_lead_id &&
    lead.lead_source_system !== "leadflow";
  const inLeadflow =
    !!lead.leadflow_lead_id || lead.lead_source_system === "leadflow";
  const syncedToCrm = lead.status === "synced_to_crm";

  // Lista editável de contatos extras (e-mails ou telefones).
  const renderExtraList = (
    field: "additional_emails" | "additional_phones",
    placeholder: string,
  ) =>
    editData[field].map((value, i) => (
      <div key={i} className="flex gap-2 items-center">
        <input
          value={value}
          onChange={(e) => {
            const arr = [...editData[field]];
            arr[i] = e.target.value;
            setEditData({ ...editData, [field]: arr });
          }}
          placeholder={placeholder}
          className={inputCls}
        />
        <button
          type="button"
          title={t("Remover")}
          onClick={() =>
            setEditData({
              ...editData,
              [field]: editData[field].filter((_, j) => j !== i),
            })
          }
          className={cn(CARD_ACTION_DANGER, "w-9 h-9 px-0 shrink-0")}
        >
          <X size={14} />
        </button>
      </div>
    ));

  const infoRow = (
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
  ) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <span className="mt-0.5 text-purple-400 shrink-0">{icon}</span>
      <span className="text-xs font-medium text-gray-500 w-32 shrink-0 pt-0.5">
        {label}
      </span>
      <div className="text-sm text-gray-900 min-w-0 flex-1 break-words">
        {value}
      </div>
    </div>
  );

  const statusPill = (ok: boolean, okLabel: string, offLabel: string) => (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border",
        ok
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-gray-50 text-gray-500 border-gray-200",
      )}
    >
      <span
        className={cn(
          "w-1.5 h-1.5 rounded-full",
          ok ? "bg-green-500" : "bg-gray-300",
        )}
      />
      {ok ? okLabel : offLabel}
    </span>
  );

  const tabCls = (active: boolean) =>
    cn(
      "flex items-center gap-1.5 px-1 pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
      active
        ? "text-purple-700 border-purple-600"
        : "text-gray-500 border-transparent hover:text-purple-600",
    );

  return (
    <Modal
      open={!!lead}
      onClose={onClose}
      closeOnOverlayClick
      title={t("Dossiê Detalhado do Lead")}
      subtitle={t(
        "Análise completa de origem, qualificação e integridade dos dados capturados.",
      )}
      maxWidth="max-w-4xl"
      footer={
        <div className="flex items-center justify-end w-full gap-2">
          {editing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(false)}
              >
                {t("Cancelar")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                isLoading={saving}
                icon={<Save size={14} />}
              >
                {t("Salvar")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onClose}>
                {t("Fechar")}
              </Button>
              {onLeadUpdate && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setEditing(true)}
                  icon={<Pencil size={14} />}
                >
                  {t("Editar Dados")}
                </Button>
              )}
            </>
          )}
        </div>
      }
    >
      {/* Cabeçalho: identidade + abas */}
      <div className={cn(cardCls, "pb-0 mb-5")}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center text-lg font-bold shrink-0">
            {(lead.name || "L").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className={labelCls}>{t("Nome completo")}</label>
                  <input
                    value={editData.name}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                    placeholder={t("Nome completo")}
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <label className={labelCls}>{t("E-mails")}</label>
                  <input
                    value={editData.email}
                    onChange={(e) =>
                      setEditData({ ...editData, email: e.target.value })
                    }
                    placeholder={t("E-mail principal")}
                    className={inputCls}
                  />
                  {renderExtraList("additional_emails", t("E-mail adicional"))}
                  <button
                    type="button"
                    onClick={() =>
                      setEditData({
                        ...editData,
                        additional_emails: [...editData.additional_emails, ""],
                      })
                    }
                    className={CARD_ACTION}
                  >
                    <Plus size={14} /> {t("Adicionar e-mail")}
                  </button>
                </div>
                <div className="space-y-2">
                  <label className={labelCls}>{t("Telefones")}</label>
                  <input
                    value={editData.phone}
                    onChange={(e) =>
                      setEditData({ ...editData, phone: e.target.value })
                    }
                    placeholder={t("Telefone principal")}
                    className={inputCls}
                  />
                  {renderExtraList(
                    "additional_phones",
                    t("Telefone adicional"),
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setEditData({
                        ...editData,
                        additional_phones: [...editData.additional_phones, ""],
                      })
                    }
                    className={CARD_ACTION}
                  >
                    <Plus size={14} /> {t("Adicionar telefone")}
                  </button>
                </div>
                {originKey && (
                  <div className="md:col-span-2">
                    <label className={labelCls}>{t("Origem do lead")}</label>
                    <select
                      aria-label={t("Origem do lead")}
                      value={editData.offline_source}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          offline_source: e.target.value,
                        })
                      }
                      className={inputCls}
                    >
                      {sourceOptions}
                    </select>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900 truncate">
                    {lead.name || t("Sem nome")}
                  </h2>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-50 border border-gray-200",
                      getScoreColor(lead.lead_score),
                    )}
                    title={t("Lead Score")}
                  >
                    <Target size={12} /> {lead.lead_score}
                  </span>
                  {lead.is_fraud && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                      <ShieldX size={12} /> {t("Suspeita de Fraude")}
                    </span>
                  )}
                  {lead.is_duplicate && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                      <Copy size={12} /> {t("Duplicado")}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-gray-600">
                  {[lead.email, ...additionalEmails].map((em, i) => (
                    <span key={`e${i}`} className="flex items-center gap-1.5">
                      <Mail size={13} className="text-gray-400" /> {em}
                    </span>
                  ))}
                  {[lead.phone, ...additionalPhones]
                    .filter(Boolean)
                    .map((ph, i) => (
                      <span key={`p${i}`} className="flex items-center gap-1.5">
                        <Phone size={13} className="text-gray-400" /> {ph}
                      </span>
                    ))}
                  <span className="flex items-center gap-1.5 text-gray-400 font-mono text-xs">
                    <Fingerprint size={13} /> #{lead.id}
                  </span>
                  {originLabel && (
                    <span className="text-xs text-gray-500">
                      {t("Origem")}: {originLabel}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-6 mt-4 border-t border-gray-100 pt-3">
          <button
            type="button"
            onClick={() => setActiveTab("geral")}
            className={tabCls(activeTab === "geral")}
          >
            <Briefcase size={14} /> {t("Geral")}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("revenue")}
            className={tabCls(activeTab === "revenue")}
          >
            <TrendingUp size={14} /> {t("Revenue Intelligence")}
          </button>
        </div>
      </div>

      <div className={cn("space-y-5", activeTab !== "geral" && "hidden")}>
        {/* Etapa do funil */}
        {kanbanStages.length > 0 && (
          <div className={cardCls}>
            <div className={cardTitleCls}>
              <Zap size={16} className="text-purple-500" />
              {t("Etapa no Funil")}
            </div>
            <div className="flex flex-wrap gap-2">
              {kanbanStages.map(({ key, label, color }) => {
                const isActive = lead.status === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onStageChange?.(lead.id, key)}
                    disabled={!onStageChange || isActive}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium border transition-colors hover:bg-gray-50 disabled:cursor-default"
                    style={{
                      backgroundColor: isActive ? color + "1a" : undefined,
                      borderColor: isActive ? color : "#e5e7eb",
                      color: isActive ? color : "#4b5563",
                    }}
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Origem */}
          <div className={cardCls}>
            <div className={cardTitleCls}>
              <Globe size={16} className="text-purple-500" />
              {t("Origem de Captura")}
            </div>
            {infoRow(
              <Globe size={14} />,
              t("Origem"),
              <>
                <Badge
                  variant="outline"
                  className="text-purple-700 bg-white border-purple-200 capitalize"
                >
                  {t(getPlatformName(lead.origin_platform || lead.platform))}
                </Badge>
                {lead.origin_description && (
                  <div className="text-xs text-gray-500 mt-1">
                    {lead.origin_description}
                  </div>
                )}
              </>,
            )}
            {lead.interest &&
              infoRow(
                <Tag size={14} />,
                t("Interesse / Produto"),
                lead.interest,
              )}
            {infoRow(
              <BarChart3 size={14} />,
              t("Campanha / Anúncio"),
              lead.campaign ? (
                <>
                  <div className="font-medium">{lead.campaign.name}</div>
                  {lead.campaign.source_post_id && (
                    <div className="text-xs text-purple-600">
                      {t("ID do Post:")} #{lead.campaign.source_post_id}
                    </div>
                  )}
                </>
              ) : (
                <span className="text-gray-400">{t("Cadastro manual")}</span>
              ),
            )}
            {infoRow(
              <Activity size={14} />,
              t("Data Captura"),
              formatDate(lead.created_at),
            )}
          </div>

          {/* Dados profissionais */}
          <div className={cardCls}>
            <div className={cardTitleCls}>
              <Briefcase size={16} className="text-purple-500" />
              {t("Dados Profissionais")}
            </div>
            {editing ? (
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>{t("Empresa")}</label>
                  <input
                    value={editData.company}
                    onChange={(e) =>
                      setEditData({ ...editData, company: e.target.value })
                    }
                    placeholder={t("Nome da empresa")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t("Cargo / Função")}</label>
                  <input
                    value={editData.job_title}
                    onChange={(e) =>
                      setEditData({ ...editData, job_title: e.target.value })
                    }
                    placeholder={t("Cargo ou função")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t("Observações")}</label>
                  <textarea
                    value={editData.notes}
                    onChange={(e) =>
                      setEditData({ ...editData, notes: e.target.value })
                    }
                    placeholder={t("Anotações sobre o lead...")}
                    rows={3}
                    className={cn(inputCls, "h-auto py-2 resize-none")}
                  />
                </div>
              </div>
            ) : (
              <>
                {infoRow(
                  <Building2 size={14} />,
                  t("Empresa"),
                  lead.company || (
                    <span className="text-gray-400">{t("Não informada")}</span>
                  ),
                )}
                {infoRow(
                  <Target size={14} />,
                  t("Cargo / Função"),
                  lead.job_title || (
                    <span className="text-gray-400">{t("Não informado")}</span>
                  ),
                )}
                {infoRow(
                  <Pencil size={14} />,
                  t("Observações"),
                  lead.notes ? (
                    <span className="whitespace-pre-line">{lead.notes}</span>
                  ) : (
                    <span className="text-gray-400">{t("Nenhuma")}</span>
                  ),
                )}
              </>
            )}
          </div>
        </div>

        {/* Tags */}
        {(availableTags.length > 0 || assignedTags.length > 0) && (
          <div className={cardCls}>
            <div className={cardTitleCls}>
              <Tag size={16} className="text-purple-500" />
              {t("Tags")}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {assignedTags.map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium border"
                  style={{
                    backgroundColor: tag.color + "18",
                    borderColor: tag.color + "55",
                    color: tag.color,
                  }}
                >
                  {tag.name}
                  {onTagsChange && (
                    <button
                      type="button"
                      onClick={() => toggleTag(tag.id, true)}
                      className="hover:opacity-70 transition-opacity"
                      title={t("Remover tag")}
                    >
                      <X size={12} />
                    </button>
                  )}
                </span>
              ))}

              {onTagsChange && unassignedTags.length > 0 && (
                <div className="relative" ref={tagDropRef}>
                  <button
                    type="button"
                    onClick={() => setTagDropdownOpen((o) => !o)}
                    className={cn(CARD_ACTION, "h-7 border-dashed")}
                  >
                    <Plus size={12} /> {t("Adicionar")}
                  </button>
                  {tagDropdownOpen && (
                    <div className="absolute left-0 top-full mt-1 z-10 bg-white border border-purple-100 rounded-xl shadow-lg p-1.5 min-w-[160px]">
                      {unassignedTags.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            toggleTag(tag.id, false);
                            setTagDropdownOpen(false);
                          }}
                          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors text-left"
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {assignedTags.length === 0 && !onTagsChange && (
                <span className="text-sm text-gray-400">
                  {t("Nenhuma tag atribuída")}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Integrações: cada uma numa linha, com status e a sua ação */}
        <div className={cardCls}>
          <div className={cardTitleCls}>
            <Activity size={16} className="text-purple-500" />
            {t("Integridade e Sincronização")}
          </div>
          <div className="divide-y divide-gray-100">
            <div className="flex flex-wrap items-center gap-3 py-3 first:pt-0">
              <Zap size={16} className="text-purple-400 shrink-0" />
              <span className="text-sm font-medium text-gray-900">
                {t("CRM Hub")}
              </span>
              {statusPill(syncedToCrm, t("Finalizado"), t("Pendente"))}
              {onSyncCRM && (
                <button
                  type="button"
                  onClick={() => onSyncCRM(lead.id)}
                  className={cn(CARD_ACTION, "ml-auto")}
                >
                  <Zap size={14} /> {t("Sincronizar CRM Hub")}
                </button>
              )}
            </div>

            {onSyncCapi && (
              <div className="flex flex-wrap items-center gap-3 py-3">
                <ShieldAlert size={16} className="text-purple-400 shrink-0" />
                <span className="text-sm font-medium text-gray-900">
                  {t("Conversions API (Meta)")}
                </span>
                <button
                  type="button"
                  onClick={() => onSyncCapi(lead.id)}
                  className={cn(CARD_ACTION, "ml-auto")}
                >
                  <ShieldAlert size={14} /> {t("Sincronizar CAPI")}
                </button>
              </div>
            )}

            {(inLeadflow || canSendToLeadflow) && (
              <div className="flex flex-wrap items-center gap-3 py-3 last:pb-0">
                <Activity size={16} className="text-purple-400 shrink-0" />
                <span className="text-sm font-medium text-gray-900">
                  {t("LeadFlow")}
                </span>
                {statusPill(
                  inLeadflow,
                  t("Enviado"),
                  lead.leadflow_sync_status === "pending"
                    ? t("Aguardando LeadFlow")
                    : t("Não enviado"),
                )}
                {canSendToLeadflow && (
                  <div className="flex flex-wrap items-center gap-2 ml-auto">
                    {!lead.metadata?.offline_source && (
                      <select
                        aria-label={t("Como o lead chegou")}
                        value={leadflowSource}
                        onChange={(e) => setLeadflowSource(e.target.value)}
                        className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-purple-300"
                      >
                        <option value="">{t("Como o lead chegou")}</option>
                        {sourceOptions}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        setSendingToLeadflow(true);
                        try {
                          await onSendToLeadflow!(
                            lead.id,
                            leadflowSource || undefined,
                          );
                        } finally {
                          setSendingToLeadflow(false);
                        }
                      }}
                      disabled={
                        sendingToLeadflow ||
                        !hasRealContact(lead) ||
                        (!lead.metadata?.offline_source && !leadflowSource)
                      }
                      title={
                        hasRealContact(lead)
                          ? lead.leadflow_sync_status === "pending"
                            ? t("Aguardando o LeadFlow; pode tentar de novo")
                            : undefined
                          : t(
                              "Cadastre um e-mail ou telefone do lead antes de enviar",
                            )
                      }
                      className={CARD_ACTION}
                    >
                      <Activity size={14} />
                      {sendingToLeadflow
                        ? t("Enviando...")
                        : lead.leadflow_sync_status === "pending"
                          ? t("Tentar de novo")
                          : t("Enviar ao LeadFlow")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={cn("space-y-6", activeTab !== "revenue" && "hidden")}>
        <div className="bg-linear-to-br from-indigo-600 to-indigo-900 rounded-4xl p-8 text-white relative overflow-hidden shadow-xl shadow-indigo-500/20">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <LineChart size={120} className="text-white" />
          </div>
          <div className="relative z-10 flex flex-col gap-2">
            <h2 className="text-2xl font-black">
              {t("Análise de Atribuição e Lift")}
            </h2>
            <p className="text-indigo-200 text-sm font-bold max-w-lg leading-relaxed">
              {t(
                "Distribuição de receita ao longo dos touchpoints da jornada baseada em Cadeias de Markov / Decaimento de Tempo.",
              )}
            </p>
          </div>
        </div>

        {lead.lead_source_system === "leadflow" && (
          <div className="bg-white border border-purple-200 rounded-3xl p-6 shadow-xs">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100">
                <Activity size={18} />
              </div>
              <div>
                <h3 className="text-[11px] font-black text-gray-900 uppercase tracking-widest">
                  {t("Auditoria Leadflow")}
                </h3>
                <p className="text-[10px] text-gray-400 font-bold">
                  {t("Histórico completo direto da plataforma Virtus Leadflow")}
                </p>
              </div>
            </div>

            {loadingLeadflowAudit ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              </div>
            ) : leadflowAuditError ? (
              <p className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                {leadflowAuditError}
              </p>
            ) : leadflowAudit && leadflowAudit.length > 0 ? (
              <ul className="space-y-2">
                {leadflowAudit.map((entry, i) => (
                  <li
                    key={i}
                    className="flex items-start justify-between gap-3 text-xs border-b border-gray-50 last:border-0 pb-2 last:pb-0"
                  >
                    <div>
                      <span className="font-black text-gray-800">
                        {entry.event || entry.status || t("Evento")}
                      </span>
                      {entry.description && (
                        <p className="text-gray-500 mt-0.5">
                          {entry.description}
                        </p>
                      )}
                    </div>
                    {entry.timestamp && (
                      <span className="text-[10px] font-bold text-gray-400 whitespace-nowrap">
                        {new Date(entry.timestamp).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-400 italic">
                {t("Nenhum evento de auditoria disponível.")}
              </p>
            )}
          </div>
        )}

        {loadingRevenue ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : revenueData?.touchpoints?.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white border border-indigo-100 rounded-3xl p-6 shadow-xs">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-100">
                    <DollarSign size={20} />
                  </div>
                  <h3 className="text-[11px] font-black text-gray-900 uppercase tracking-widest">
                    {t("Receita Total")}
                  </h3>
                </div>
                <div className="text-3xl font-black text-indigo-600 mt-4">
                  {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(revenueData.total_revenue)}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest pl-2">
                {t("Jornada do Cliente (Touchpoints)")}
              </h3>
              <div className="bg-white border border-indigo-100 rounded-3xl p-6 shadow-xs space-y-6">
                {revenueData.touchpoints.map((tp: any, i: number) => (
                  <div key={tp.id} className="relative">
                    {i !== revenueData.touchpoints.length - 1 && (
                      <div className="absolute left-[19px] top-[40px] bottom-[-24px] w-0.5 bg-indigo-100" />
                    )}
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-100 z-10 shrink-0">
                        <BarChart3 size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-gray-900">
                              {tp.channel}
                            </span>
                            <span className="text-[10px] font-bold text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-lg border border-indigo-100">
                              {tp.interaction_type}
                            </span>
                          </div>
                          <span className="text-xs font-bold text-gray-400">
                            {new Date(tp.timestamp).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <div className="text-xs font-bold text-gray-500 mb-3">
                          {tp.campaign || tp.source || t("Orgânico / Direto")}
                        </div>

                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-gray-400">
                            <span>
                              {t("Peso na Conversão")} (
                              {(tp.attribution_weight * 100).toFixed(1)}%)
                            </span>
                            <span className="text-indigo-600">
                              {new Intl.NumberFormat("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              }).format(tp.revenue_credit)}
                            </span>
                          </div>
                          <div className="w-full bg-indigo-50 rounded-full h-2">
                            <div
                              className="bg-indigo-500 h-2 rounded-full transition-all"
                              style={{
                                width: `${Math.max(2, tp.attribution_weight * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-dashed border-indigo-200 rounded-3xl p-12 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-indigo-50 text-indigo-300 rounded-2xl flex items-center justify-center mb-4">
              <TrendingUp size={32} />
            </div>
            <h3 className="text-lg font-black text-gray-900 mb-2">
              {t("Nenhuma jornada mapeada")}
            </h3>
            <p className="text-sm text-gray-500 font-bold max-w-md">
              {t(
                "Este lead ainda não tem pontos de contato mapeados na API de Revenue Intelligence ou não gerou receita.",
              )}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CampaignLeadDetailsModal;
